import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrandMark } from '../components/Brand.js';
import type { AuthUser } from '@crosscastr/shared';
import { activateApi, ActivateError, type ActivateCheckResponse } from '../lib/activateApi.js';
import { setSession } from '../lib/auth.js';

// Customer first-login activation (public). Opened from the invite link. Flow: check the token →
// "התחל" sends an SMS-OTP → enter the code + choose a password → the owner is activated and logged in,
// then dropped into their tenant dashboard. Reuses the LoginPage visual system. The token stays in the
// path only — it is never printed in the page text and every call sends it in a POST body.

const MIN_PASSWORD_LENGTH = 10; // mirrors the API (shared/auth/password.MIN_PASSWORD_LENGTH)

const wrap: React.CSSProperties = {
  minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24,
  background: 'var(--bg)', backgroundImage: 'var(--canvas-tint)',
};
const card: React.CSSProperties = {
  width: '100%', maxWidth: 440, background: 'var(--surface-2)', border: '1px solid var(--hair)',
  borderRadius: 'var(--r-card)', boxShadow: 'var(--sh-hero)', padding: 32,
};
const label: React.CSSProperties = { fontSize: 13, color: 'var(--text-dim)', marginBottom: 8, fontWeight: 600, display: 'block' };
const input: React.CSSProperties = {
  width: '100%', height: 46, padding: '0 14px', borderRadius: 'var(--pill)', border: '1px solid var(--hair)',
  background: 'var(--surface-1)', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 15,
};

type Phase = 'loading' | 'invalid' | 'intro' | 'form';

export function ActivatePage(): JSX.Element {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('loading');
  const [info, setInfo] = useState<ActivateCheckResponse | null>(null);
  const [challengeId, setChallengeId] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    activateApi
      .check(token)
      .then((r) => {
        if (!alive) return;
        setInfo(r);
        setPhase('intro');
      })
      .catch(() => {
        if (alive) setPhase('invalid');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  async function start(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const r = await activateApi.start(token);
      setChallengeId(r.challengeId);
      setPhoneHint(r.phoneHint);
      setPhase('form');
    } catch (err) {
      setError(err instanceof ActivateError && err.code === 'sms_send_failed'
        ? 'שליחת קוד ה-SMS נכשלה. נסה שוב בעוד רגע.'
        : 'הקישור אינו תקף או שכבר נוצל.');
    } finally {
      setBusy(false);
    }
  }

  async function complete(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('הסיסמאות אינן תואמות');
      return;
    }
    setBusy(true);
    try {
      const s = await activateApi.complete(token, challengeId, code.trim(), password);
      const user: AuthUser = {
        id: s.user.id, email: s.user.email, name: s.user.name,
        role: s.user.role as AuthUser['role'], mustChangePassword: s.user.mustChangePassword,
      };
      setSession(s.accessToken, s.refreshToken, user);
      navigate('/', { replace: true });
    } catch (err) {
      const code2 = err instanceof ActivateError ? err.code : 'error';
      setError(
        code2 === 'invalid_code' ? 'הקוד שגוי או פג תוקף'
          : code2 === 'already_activated' ? 'החשבון כבר הופעל. אפשר להיכנס במסך הכניסה.'
            : 'ההפעלה נכשלה. נסה שוב.',
      );
    } finally {
      setBusy(false);
    }
  }

  const pwOk = password.length >= MIN_PASSWORD_LENGTH;
  const pwHint = password.length === 0 ? '' : pwOk ? 'סיסמה תקינה' : `לפחות ${MIN_PASSWORD_LENGTH} תווים`;

  return (
    <div style={wrap} dir="rtl">
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <BrandMark size={40} />
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, letterSpacing: 'var(--display-track)' }}>
            Cross<b>Castr</b>
          </div>
        </div>

        {phase === 'loading' && <p style={{ color: 'var(--text-dim)', fontSize: 14 }}>רק רגע…</p>}

        {phase === 'invalid' && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, margin: '4px 0 8px' }}>הקישור אינו תקף</h1>
            <p style={{ color: 'var(--text-dim)', fontSize: 14 }}>
              הקישור אינו תקף או שכבר נוצל. פנה למי ששלח לך את ההזמנה לקבלת קישור חדש.
            </p>
          </>
        )}

        {phase === 'intro' && info && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, margin: '4px 0 6px' }}>
              ברוך הבא ל-CrossCastr
            </h1>
            <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 20 }}>
              נגדיר לך את החשבון של <b style={{ color: 'var(--text)' }}>{info.tenantName}</b>. נשלח קוד אימות ל-SMS,
              ואז תבחר סיסמה.
            </p>
            <div className="card soft" style={{ padding: 16, marginBottom: 22 }}>
              <div className="gm-row"><span className="k">חברה</span><span className="v">{info.tenantName}</span></div>
              <div className="gm-row" style={{ marginTop: 8 }}><span className="k">אימייל</span><span className="v ltr">{info.email}</span></div>
            </div>
            <button className="btn" onClick={start} disabled={busy} style={{ width: '100%', height: 46 }}>
              {busy ? 'שולח קוד…' : 'התחל'}
            </button>
          </>
        )}

        {phase === 'form' && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, margin: '4px 0 6px' }}>קביעת סיסמה</h1>
            <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 22 }}>
              שלחנו קוד בן 6 ספרות למספר שמסתיים ב-{phoneHint}. הזן אותו ובחר סיסמה.
            </p>
            <form onSubmit={complete}>
              <label style={label} htmlFor="act-code">קוד אימות</label>
              <input id="act-code" style={{ ...input, marginBottom: 18, letterSpacing: 8, textAlign: 'center', fontFamily: 'var(--font-mono)' }}
                type="text" inputMode="numeric" dir="ltr" autoComplete="one-time-code" maxLength={6} placeholder="000000"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required />

              <label style={label} htmlFor="act-pw">בחירת סיסמה</label>
              <input id="act-pw" style={{ ...input, marginBottom: 6 }} type="password" dir="ltr" autoComplete="new-password"
                value={password} onChange={(e) => setPassword(e.target.value)} required />
              {pwHint && <div style={{ fontSize: 12, color: pwOk ? 'var(--ok)' : 'var(--text-dim)', marginBottom: 14 }}>{pwHint}</div>}

              <label style={label} htmlFor="act-pw2">אימות סיסמה</label>
              <input id="act-pw2" style={{ ...input, marginBottom: 22 }} type="password" dir="ltr" autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} required />

              <button className="btn" type="submit" disabled={busy || code.length !== 6 || !pwOk || !confirm} style={{ width: '100%', height: 46 }}>
                {busy ? 'מפעיל…' : 'סיום והתחברות'}
              </button>
            </form>
          </>
        )}

        {error && (
          <div style={{ marginTop: 18, padding: '10px 14px', borderRadius: 'var(--r-sm)', background: 'color-mix(in srgb, var(--risk) 12%, transparent)', color: 'var(--risk)', fontSize: 14 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
